import type { HomeDashboardRepository, HomeDashboardState } from "./home-dashboard";
import type { MealRepository } from "./meal-repository";
import type { MealType } from "../domain/model";
import { loadActivity, type ActivityEntry } from "./activity";
import { statisticsRules } from "../../backend/src/rules/statistics";

export type StatisticsPeriodKind = "week" | "month" | "quarter" | "year" | "custom";
export interface StatisticsPeriod { kind: StatisticsPeriodKind; start: string; end: string; label: string }
export interface StatisticsDay { date: string; dashboard: HomeDashboardState; activity: ActivityEntry | null; products: Array<{ name: string; calories: number }> }
export interface StatisticsSummary {
  period: StatisticsPeriod;
  days: StatisticsDay[];
  loggedDays: number;
  activityDays: number;
  averages: { calories: number; protein: number; fat: number; carbs: number; burned: number; steps: number | null };
  goals: { calories: number | null; protein: number | null; fat: number | null; carbs: number | null };
  mealShares: Record<MealType, number>;
  topProducts: Array<{ name: string; calories: number; count: number }>;
  recommendations: string[];
}

const iso = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const date = (value: string) => new Date(`${value}T12:00:00`);
const shift = (value: string, amount: number) => { const next = date(value); next.setDate(next.getDate() + amount); return iso(next); };
export const statisticsDates = (start: string, end: string) => {
  const valid = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(date(value).getTime()) && iso(date(value)) === value;
  if (!valid(start) || !valid(end) || start > end) throw new Error("Проверьте начало и конец периода");
  const values: string[] = [];
  for (let value = start; value <= end; value = shift(value, 1)) {
    if (values.length >= 366) throw new Error("Выберите период не длиннее 366 дней");
    values.push(value);
  }
  return values;
};
const round = (value: number) => Math.round(value);
const average = (values: number[]) => values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
const monthName = (value: Date) => new Intl.DateTimeFormat("ru-RU", { month: "long", year: "numeric" }).format(value).replace(/\sг\.$/u, "");

export function completedPeriod(kind: Exclude<StatisticsPeriodKind, "custom">, today: string): StatisticsPeriod {
  const current = date(today);
  if (kind === "week") {
    const mondayOffset = (current.getDay() + 6) % 7;
    const end = shift(today, -(mondayOffset + 1));
    const start = shift(end, -6);
    return { kind, start, end, label: `${new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short" }).format(date(start))} — ${new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short" }).format(date(end))}` };
  }
  if (kind === "month") {
    const endDate = new Date(current.getFullYear(), current.getMonth(), 0, 12);
    const startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1, 12);
    return { kind, start: iso(startDate), end: iso(endDate), label: monthName(startDate) };
  }
  if (kind === "quarter") {
    const currentQuarter = Math.floor(current.getMonth() / 3);
    const endDate = new Date(current.getFullYear(), currentQuarter * 3, 0, 12);
    const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 2, 1, 12);
    const quarter = Math.floor(startDate.getMonth() / 3) + 1;
    return { kind, start: iso(startDate), end: iso(endDate), label: `${quarter} квартал ${startDate.getFullYear()}` };
  }
  const year = current.getFullYear() - 1;
  return { kind, start: `${year}-01-01`, end: `${year}-12-31`, label: `${year} год` };
}

export function recentPeriod(days: number, today: string): StatisticsPeriod {
  return { kind: "custom", start: shift(today, -(days - 1)), end: today, label: `${days} дней` };
}

export async function loadStatistics(period: StatisticsPeriod, dashboards: HomeDashboardRepository, meals: MealRepository): Promise<StatisticsSummary> {
  const days: StatisticsDay[] = [];
  const dates = statisticsDates(period.start, period.end);
  for (let offset = 0; offset < dates.length; offset += 14) days.push(...await Promise.all(dates.slice(offset, offset + 14).map(async currentDate => {
    const [dashboard, mealDay] = await Promise.all([dashboards.get(currentDate), meals.getDay(currentDate)]);
    return { date: currentDate, dashboard, activity: loadActivity(currentDate), products: mealDay.items.filter(item => !item.deletedAt).map(item => ({ name: item.displayName, calories: item.calories })) };
  })));
  return summarizeStatistics(period, days);
}

export function summarizeStatistics(period: StatisticsPeriod, days: StatisticsDay[]): StatisticsSummary {
  const nutritionDays = days.filter(day => day.products.length > 0);
  const activityDays = days.filter(day => day.activity !== null);
  const products = new Map<string, { name: string; calories: number; count: number }>();
  for (const day of days) for (const product of day.products) { const key = product.name.toLocaleLowerCase("ru-RU"); const old = products.get(key) ?? { name: product.name, calories: 0, count: 0 }; products.set(key, { ...old, calories: old.calories + product.calories, count: old.count + 1 }); }
  const goalAverage = (read: (day: StatisticsDay) => number | null) => nutritionDays.length && nutritionDays.every(day => read(day) !== null) ? round(average(nutritionDays.map(day => read(day)!))) : null;
  const goals = {
    calories: goalAverage(day => day.dashboard.calorieGoal === null ? null : day.dashboard.calorieGoal + day.dashboard.activityCalorieBonus),
    protein: goalAverage(day => day.dashboard.macros.protein.goal),
    fat: goalAverage(day => day.dashboard.macros.fat.goal),
    carbs: goalAverage(day => day.dashboard.macros.carbs.goal),
  };
  const averages = {
    calories: round(average(nutritionDays.map(day => day.dashboard.eatenCalories))),
    protein: round(average(nutritionDays.map(day => day.dashboard.macros.protein.consumed))),
    fat: round(average(nutritionDays.map(day => day.dashboard.macros.fat.consumed))),
    carbs: round(average(nutritionDays.map(day => day.dashboard.macros.carbs.consumed))),
    burned: round(average(activityDays.map(day => day.dashboard.burnedCalories))),
    steps: activityDays.length ? round(average(activityDays.map(day => day.activity!.steps))) : null,
  };
  const mealTotals = { breakfast: 0, lunch: 0, dinner: 0, snack: 0 } satisfies Record<MealType, number>;
  days.forEach(day => (Object.keys(mealTotals) as MealType[]).forEach(type => { mealTotals[type] += day.dashboard.meals[type].calories; }));
  const mealTotal = Object.values(mealTotals).reduce((sum, value) => sum + value, 0);
  const mealShares = Object.fromEntries(Object.entries(mealTotals).map(([key, value]) => [key, mealTotal ? value / mealTotal : 0])) as Record<MealType, number>;
  const recommendations: string[] = [];
  const enough = nutritionDays.length >= statisticsRules.minimumLoggedDays && nutritionDays.length / days.length >= statisticsRules.minimumCoverage;
  if (!enough) recommendations.push(`Заполнено ${nutritionDays.length} из ${days.length} дней. Для уверенных выводов нужно больше записей.`);
  else {
    if (nutritionDays.some(day => day.dashboard.nutritionIncomplete)) recommendations.push("У части продуктов неполная пищевая ценность. Уточните её перед сравнением с целью.");
    else {
      if (goals.calories && averages.calories > goals.calories * statisticsRules.calorieUpper) recommendations.push(`По записям среднее превышение ориентира с активностью — ${round(averages.calories - goals.calories)} ккал. Посмотрите на самые калорийные продукты периода.`);
      if (goals.calories && averages.calories < goals.calories * statisticsRules.calorieLower) recommendations.push(`В записях на ${round(goals.calories - averages.calories)} ккал меньше дневного ориентира. Сначала проверьте, вся ли еда записана.`);
      if (goals.protein && averages.protein < goals.protein * statisticsRules.proteinLower) recommendations.push(`Белка в записях в среднем на ${round(goals.protein - averages.protein)} г меньше вашей цели. Если дневник полный, обратите внимание на белковые продукты.`);
      if (goals.fat && averages.fat > goals.fat * statisticsRules.fatUpper) recommendations.push(`Жиров в среднем на ${round(averages.fat - goals.fat)} г больше вашей цели. Проверьте порции и добавки в дневнике.`);
      if (!recommendations.length) recommendations.push(goals.calories ? "Средние значения не пересекли пороги подсказок. Это не оценка полноценности рациона или стабильности каждого дня." : "Задайте цель в профиле, чтобы сравнивать записи с личными ориентирами.");
    }
  }
  return { period, days, loggedDays: nutritionDays.length, activityDays: activityDays.length, averages, goals, mealShares, topProducts: [...products.values()].sort((a, b) => b.calories - a.calories).slice(0, 5), recommendations: recommendations.slice(0, 3) };
}
